import { T, STATUS_CFG, SEV_COLOR } from "../utils/theme";
import { AlertCircle, CheckCircle, Info, X, RefreshCw, ChevronUp, ChevronDown } from "lucide-react";

// ─── Card ─────────────────────────────────────────────────────────────────────
export const Card = ({ children, style = {}, className = "", onClick }) => (
  <div
    className={className}
    onClick={onClick}
    style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 14,
      ...style,
    }}
  >{children}</div>
);

// ─── KPI Card ─────────────────────────────────────────────────────────────────
export const KpiCard = ({ label, value, icon: Icon, color, sub, trend, trendUp, delay = 0 }) => (
  <Card
    className="hov-card fade-up"
    style={{
      padding: 22, cursor: "default", transition: "all .2s",
      animationDelay: `${delay}ms`,
      position: "relative", overflow: "hidden",
    }}
  >
    {/* bg glow */}
    <div style={{
      position: "absolute", right: -20, bottom: -20,
      width: 100, height: 100, borderRadius: "50%",
      background: `${color}10`, filter: "blur(20px)", pointerEvents: "none",
    }} />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, position: "relative" }}>
      <span style={{ fontSize: 11, color: T.textSub, letterSpacing: ".04em", textTransform: "uppercase" }}>{label}</span>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={16} color={color} />
      </div>
    </div>
    <div style={{ fontFamily: T.display, fontSize: 30, fontWeight: 900, color: T.text, lineHeight: 1, marginBottom: 10, position: "relative" }}>{value}</div>
    {(trend || sub) && (
      <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
        {trend && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: trendUp ? T.green : T.red }}>
            {trendUp ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {trend}
          </span>
        )}
        {sub && <span style={{ fontSize: 11, color: T.muted }}>{sub}</span>}
      </div>
    )}
  </Card>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
export const StatusBadge = ({ status }) => {
  const cfg = STATUS_CFG[status] || { label: status, color: T.muted, bg: `${T.muted}18` };
  return (
    <span className="badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
  );
};

// ─── Severity Badge ───────────────────────────────────────────────────────────
export const SeverityBadge = ({ severity }) => {
  const c = SEV_COLOR[severity] || T.muted;
  return <span className="badge" style={{ color: c, background: `${c}18` }}>{severity || "—"}</span>;
};

// ─── CVSS Indicator ───────────────────────────────────────────────────────────
export const CvssScore = ({ score }) => {
  if (!score && score !== 0) return <span style={{ color: T.muted }}>—</span>;
  const color = score >= 9 ? T.red : score >= 7 ? T.orange : score >= 4 ? T.yellow : T.green;
  return (
    <span style={{
      fontFamily: T.display, fontWeight: 800, fontSize: 14, color,
      background: `${color}15`, borderRadius: 5, padding: "2px 8px",
    }}>{score.toFixed(1)}</span>
  );
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
export const Spinner = ({ size = 32, center = true }) => {
  const el = (
    <div style={{
      width: size, height: size,
      border: `${size > 20 ? 3 : 2}px solid ${T.border}`,
      borderTopColor: T.blue,
      borderRadius: "50%",
      animation: "spin .7s linear infinite",
      flexShrink: 0,
    }} />
  );
  return center ? <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 48 }}>{el}</div> : el;
};

// ─── Empty State ──────────────────────────────────────────────────────────────
export const EmptyState = ({ icon: Icon, title = "Aucune donnée", sub }) => (
  <div style={{ textAlign: "center", padding: "52px 24px" }}>
    <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${T.blue}0f`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
      <Icon size={24} color={T.muted} />
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: T.textSub, marginBottom: 4 }}>{title}</div>
    {sub && <div style={{ fontSize: 12, color: T.muted }}>{sub}</div>}
  </div>
);

// ─── Toast Stack ──────────────────────────────────────────────────────────────
export const ToastStack = ({ toasts, remove }) => (
  <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8 }}>
    {toasts.map(t => (
      <div key={t.id} className="fade-up" style={{
        display: "flex", alignItems: "center", gap: 10,
        background: t.type === "error" ? "#2a0a10" : t.type === "warning" ? "#1f1500" : "#091a12",
        border: `1px solid ${t.type === "error" ? T.red : t.type === "warning" ? T.yellow : T.green}55`,
        borderRadius: 11, padding: "11px 18px",
        boxShadow: "0 8px 32px rgba(0,0,0,.5)",
        color: t.type === "error" ? T.red : t.type === "warning" ? T.yellow : T.green,
        fontSize: 13, fontWeight: 500, maxWidth: 360,
      }}>
        {t.type === "error" ? <AlertCircle size={15} /> : t.type === "warning" ? <Info size={15} /> : <CheckCircle size={15} />}
        <span style={{ flex: 1 }}>{t.msg}</span>
        <button onClick={() => remove(t.id)} style={{ background: "none", border: "none", color: "inherit", opacity: .6, cursor: "pointer", padding: 0 }}>
          <X size={13} />
        </button>
      </div>
    ))}
  </div>
);

// ─── Modal ────────────────────────────────────────────────────────────────────
export const Modal = ({ open, onClose, title, children, width = 520 }) => {
  if (!open) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
          <h3 style={{ fontFamily: T.display, fontWeight: 700, fontSize: 17, color: T.text }}>{title}</h3>
          <button onClick={onClose} className="btn btn-ghost btn-icon"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

// ─── Section Header ───────────────────────────────────────────────────────────
export const SectionHeader = ({ title, sub, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
    <div>
      <h2 style={{ fontFamily: T.display, fontWeight: 800, fontSize: 18, color: T.text, marginBottom: 3 }}>{title}</h2>
      {sub && <p style={{ fontSize: 12, color: T.muted }}>{sub}</p>}
    </div>
    {action}
  </div>
);

// ─── Search Input ─────────────────────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = "Rechercher...", style = {} }) => (
  <div style={{ position: "relative", ...style }}>
    <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", opacity: .4 }}
      width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth={2.5}>
      <circle cx={11} cy={11} r={8} /><path d="m21 21-4.35-4.35" />
    </svg>
    <input
      value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ paddingLeft: 36, background: T.surface, ...style }}
    />
  </div>
);

// ─── Tabs ─────────────────────────────────────────────────────────────────────
export const Tabs = ({ tabs, active, onChange }) => (
  <div style={{ display: "flex", gap: 4, background: T.surface, borderRadius: 10, padding: 4 }}>
    {tabs.map(tab => (
      <button key={tab.id} onClick={() => onChange(tab.id)} style={{
        border: "none", borderRadius: 7, padding: "7px 16px",
        fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all .15s",
        background: active === tab.id ? T.blue : "transparent",
        color: active === tab.id ? "#fff" : T.muted,
      }}>
        {tab.label}
        {tab.count != null && (
          <span style={{ marginLeft: 6, background: active === tab.id ? "rgba(255,255,255,.2)" : T.dim, borderRadius: 4, padding: "1px 5px", fontSize: 10 }}>
            {tab.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ─── Progress Bar ─────────────────────────────────────────────────────────────
export const ProgressBar = ({ value, max, color, height = 6, showLabel = false }) => {
  const pct = Math.min(100, (value / max) * 100) || 0;
  return (
    <div>
      {showLabel && (
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.muted, marginBottom: 5 }}>
          <span>{value.toLocaleString()}</span>
          <span>{pct.toFixed(0)}%</span>
        </div>
      )}
      <div style={{ height, background: T.border, borderRadius: height, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color || T.blue, borderRadius: height, transition: "width .6s ease" }} />
      </div>
    </div>
  );
};

// ─── Loading Button ───────────────────────────────────────────────────────────
export const LoadingBtn = ({ loading, children, className = "btn btn-primary", ...props }) => (
  <button className={className} disabled={loading} {...props}>
    {loading ? <><RefreshCw size={13} className="spin" />Chargement...</> : children}
  </button>
);

// ─── Form Field ───────────────────────────────────────────────────────────────
export const Field = ({ label, error, children, style = {} }) => (
  <div style={{ marginBottom: 14, ...style }}>
    {label && <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>}
    {children}
    {error && <div style={{ fontSize: 11, color: T.red, marginTop: 5, display: "flex", alignItems: "center", gap: 5 }}><AlertCircle size={11} />{error}</div>}
  </div>
);

// ─── Alert Box ────────────────────────────────────────────────────────────────
export const AlertBox = ({ type = "info", children }) => {
  const colors = { info: T.blue, success: T.green, warning: T.yellow, error: T.red };
  const c = colors[type] || T.blue;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 10,
      background: `${c}0f`, border: `1px solid ${c}33`,
      borderRadius: 10, padding: "12px 16px", fontSize: 12,
    }}>
      {type === "error" ? <AlertCircle size={15} color={c} style={{ flexShrink: 0, marginTop: 1 }} /> : <Info size={15} color={c} style={{ flexShrink: 0, marginTop: 1 }} />}
      <span style={{ color: c }}>{children}</span>
    </div>
  );
};
